import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import type { VariantPage } from "@/lib/api/formatting/rorosTweed/productPage";
import { convertProductToCartItem } from "@/lib/centra/selection/selectionFormatter";
import useChangeLocation from "@/lib/hooks/useChangeLocation";
import useLocation from "@/lib/hooks/useLocation";
import type { ProductPageData } from "@/pages/api/service/products/[...langAndUri]";

import UI from "./UI";

const formatProduct = (
  setter: any,
  activeProduct?: VariantPage | null,
  variants?: ProductPageData["data"]["variants"]
) => {
  const formattedProduct = { ...activeProduct };

  if (!formattedProduct) return formattedProduct;

  const swatchVariants = formattedProduct.info?.details?.variants?.map(
    (variant) => {
      if (variant.id === formattedProduct.id) return variant;

      const variantName = variants?.variants
        ? variant.colourName.toLowerCase().replace(/\W/g, "-")
        : undefined;
      const setActive = variantName ? () => setter(variantName) : () => {};

      return {
        ...variant,
        setActive,
      };
    }
  );

  if (!swatchVariants || !formattedProduct.info?.details?.variants)
    return formattedProduct;

  formattedProduct.info.details.variants = swatchVariants;

  return formattedProduct;
};
interface ProductPageProps {
  product: ProductPageData;
}

const ProductPage = ({ product }: ProductPageProps) => {
  const router = useRouter();

  const { variant, country, color } = router.query;
  const { location } = useLocation();

  const { changeLocation } = useChangeLocation();
  const updateVariant = (variant: string) => {
    const nextQuery: typeof router.query = { ...router.query, variant };
    delete nextQuery.color;
    router.push({ query: nextQuery }, undefined, { scroll: false });
  };

  const variants = product.data.variants;
  const defaultProductName = variants ? variants.default : null;
  const defaultVariant =
    variants && defaultProductName
      ? variants.variants[defaultProductName]
      : null;

  useEffect(() => {
    const colorVariant = Object.entries(variants?.variants || {})
      .map(([key, value]) => {
        return { ...value, slug: key };
      })
      .find((variant) => variant.id === color);

    if (colorVariant) {
      updateVariant(colorVariant?.slug);
    }
  }, [color]);

  const initialVariant =
    typeof variant === "string" && variants && variants.variants[variant]
      ? variants.variants[variant]
      : defaultVariant;

  const relatedProducts = product.data.relatedProducts;
  const [formattedProduct, setFormattedProduct] = useState(
    formatProduct(updateVariant, initialVariant, variants)
  );

  useEffect(() => {
    const newVariant =
      typeof variant === "string" && variants && variants.variants[variant]
        ? variants.variants[variant]
        : defaultVariant;

    if (newVariant) {
      setFormattedProduct(formatProduct(updateVariant, newVariant, variants));
    }
  }, [variant, product]);

  useEffect(() => {
    if (!country) return;
    if (country.length !== 2) return;
    if (
      location?.toLocaleLowerCase() === (country as string)?.toLocaleLowerCase()
    )
      return;

    changeLocation.mutate({
      locationId: country as string,
    });
  }, [country, location]);

  if (!formattedProduct) {
    return <></>;
  }
  const addPriceAndSize = convertProductToCartItem(formattedProduct);
  return (
    <UI
      product={formattedProduct}
      relatedProducts={relatedProducts}
      addPriceAndSize={addPriceAndSize}
    />
  );
};

export default ProductPage;
